import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from "@/components/ui/alert-dialog";
import { Plus, Trash2, ArrowRight } from "lucide-react";
import {
  ReferenceListDependencyView,
  ReferenceValueView,
  ReferenceValueLink
} from "@/entities/all";

export default function ReferenceValueLinks({ depId, listId }) {
  const [dependency, setDependency] = useState(null);
  const [parentValues, setParentValues] = useState([]);
  const [childValues, setChildValues] = useState([]);
  const [links, setLinks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedParentId, setSelectedParentId] = useState('');
  const [selectedChildId, setSelectedChildId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedLink, setSelectedLink] = useState(null);

  useEffect(() => {
    if (depId) loadData();
  }, [depId]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const deps = await ReferenceListDependencyView.filter({ id: depId });
      const dep = deps[0];
      if (!dep) return;
      setDependency(dep);

      const [pValues, cValues, allLinks] = await Promise.all([
        ReferenceValueView.filter({ list_id: dep.parent_list_id }, 'canonical_value'),
        ReferenceValueView.filter({ list_id: dep.child_list_id }, 'canonical_value'),
        ReferenceValueLink.filter({ dependency_id: depId })
      ]);

      setParentValues(pValues);
      setChildValues(cValues);
      setLinks(allLinks);
    } catch (error) {
      console.error('Error loading value links:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddLink = async () => {
    if (!selectedParentId || !selectedChildId) return;
    setIsSubmitting(true);
    try {
      await ReferenceValueLink.create({
        dependency_id: parseInt(depId),
        parent_value_id: parseInt(selectedParentId),
        child_value_id: parseInt(selectedChildId)
      });
      setSelectedParentId('');
      setSelectedChildId('');
      loadData();
    } catch (error) {
      console.error('Error creating value link:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteLink = async () => {
    if (!selectedLink) return;
    try {
      await ReferenceValueLink.delete(selectedLink.id);
      setIsDeleteDialogOpen(false);
      setSelectedLink(null);
      loadData();
    } catch (error) {
      console.error('Error deleting value link:', error);
    }
  };

  const getValueName = (valueId, values) => {
    const v = values.find(v => String(v.id) === String(valueId));
    return v?.canonical_value || `ID: ${valueId}`;
  };

  // Group links by parent value for display
  const linksByParent = {};
  links.forEach(link => {
    if (!linksByParent[link.parent_value_id]) {
      linksByParent[link.parent_value_id] = [];
    }
    linksByParent[link.parent_value_id].push(link);
  });

  if (isLoading) {
    return (
      <Card className="border-slate-200 bg-white/80 backdrop-blur-sm">
        <CardContent className="p-6">
          <div className="animate-pulse space-y-2">
            <div className="h-8 bg-slate-200 rounded w-1/3"></div>
            <div className="h-20 bg-slate-200 rounded w-full"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!dependency) return null;

  return (
    <Card className="border-blue-200 bg-blue-50/30">
      <CardHeader>
        <CardTitle className="text-lg font-semibold">
          Value Links: {dependency.parent_list_name} → {dependency.child_list_name}
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          {dependency.description || 'Link parent values to their allowed child values.'}
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Add Link Form */}
        <div className="flex items-end gap-3 p-4 rounded-lg border bg-card-bg">
          <div className="flex-1">
            <label className="text-sm font-medium mb-1 block">Parent Value ({dependency.parent_list_name})</label>
            <Select value={selectedParentId} onValueChange={setSelectedParentId}>
              <SelectTrigger>
                <SelectValue placeholder="Select parent value..." />
              </SelectTrigger>
              <SelectContent>
                {parentValues.map(v => (
                  <SelectItem key={v.id} value={String(v.id)}>
                    {v.canonical_value}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <ArrowRight className="w-5 h-5 text-slate-400 mb-2" />
          <div className="flex-1">
            <label className="text-sm font-medium mb-1 block">Child Value ({dependency.child_list_name})</label>
            <Select value={selectedChildId} onValueChange={setSelectedChildId}>
              <SelectTrigger>
                <SelectValue placeholder="Select child value..." />
              </SelectTrigger>
              <SelectContent>
                {childValues.map(v => (
                  <SelectItem key={v.id} value={String(v.id)}>
                    {v.canonical_value}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={handleAddLink} disabled={isSubmitting || !selectedParentId || !selectedChildId}>
            <Plus className="w-4 h-4 mr-1" />
            Link
          </Button>
        </div>

        {/* Existing Links */}
        {links.length === 0 ? (
          <p className="text-sm text-muted-foreground">No value links configured yet. Links restrict which child values are valid for each parent value.</p>
        ) : (
          <div className="space-y-3">
            {Object.entries(linksByParent).map(([parentId, parentLinks]) => (
              <div key={parentId} className="rounded-lg border bg-card-bg p-3">
                <div className="font-medium text-sm mb-2">
                  {getValueName(parentId, parentValues)}
                </div>
                <div className="flex flex-wrap gap-2">
                  {parentLinks.map(link => (
                    <Badge
                      key={link.id}
                      variant="secondary"
                      className="flex items-center gap-1 pr-1"
                    >
                      {getValueName(link.child_value_id, childValues)}
                      <button
                        onClick={() => { setSelectedLink(link); setIsDeleteDialogOpen(true); }}
                        className="ml-1 rounded-full hover:bg-slate-300 p-0.5"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Value Link</AlertDialogTitle>
            <AlertDialogDescription>
              Remove this value link? This will no longer restrict the child value to this parent.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setSelectedLink(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteLink} className="bg-red-600 hover:bg-red-700">
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
